/**
 * Validates a URL against allowed origins before performing a redirect.
 * Blocks cross-origin, javascript:, and data: protocol redirects by default.
 *
 * @param url - The URL to redirect to (absolute or relative)
 * @param options.additionalOrigins - Extra origins to allow beyond window.location.origin
 * @returns true if redirect was performed, false if blocked or invalid
 */
export function safeRedirect(url: string, options?: { additionalOrigins?: string[] }): boolean {
  try {
    const parsed = new URL(url, window.location.href);

    // Explicit protocol allowlist — blocks javascript:, data:, vbscript:, etc.
    // regardless of origin comparison results (defends against null-origin contexts).
    const SAFE_PROTOCOLS = ["http:", "https:"];
    if (!SAFE_PROTOCOLS.includes(parsed.protocol)) {
      console.warn(`[safeRedirect] Blocked redirect to disallowed protocol: ${url}`);
      return false;
    }

    const normalizedExtra = (options?.additionalOrigins ?? []).map(o => {
      try {
        return new URL(o).origin;
      } catch {
        return o;
      }
    });
    const allowedOrigins = [window.location.origin, ...normalizedExtra];

    if (allowedOrigins.includes(parsed.origin)) {
      window.location.href = parsed.href;
      return true;
    }

    console.warn(`[safeRedirect] Blocked redirect to disallowed origin: ${url}`);
    return false;
  } catch {
    console.warn(`[safeRedirect] Blocked redirect due to malformed URL: ${url}`);
    return false;
  }
}
